import type { PartnerDirectoryMappedExpertPayload } from "./partnerDirectoryApi.service";

// Mirrors the crawler's normalizeMappedOverrides comparison rules so the
// mapper UI highlights exactly the fields the server would persist as
// overrides: trimmed-string compare, null == '' == empty, numeric compare
// with an epsilon, and Set compare (order- and duplicate-insensitive) for
// languages.

export const MAPPED_SCALAR_KEYS = ["agency_name", "first_name", "last_name", "bio", "country", "starting_price", "timezone", "phone_no"] as const;
export const MAPPED_SOCIAL_LINK_KEYS = ["website", "shopify_directory_link", "linkedin_url", "x_url"] as const;

export type MappedScalarKey = (typeof MAPPED_SCALAR_KEYS)[number];
export type MappedSocialLinkKey = (typeof MAPPED_SOCIAL_LINK_KEYS)[number];

const NUMERIC_COMPARE_EPSILON = 1e-9;

function isEmptyScalar(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

function toComparableNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function mappedScalarEquals(a: unknown, b: unknown): boolean {
  if (isEmptyScalar(a) || isEmptyScalar(b)) return isEmptyScalar(a) && isEmptyScalar(b);

  const aNumber = toComparableNumber(a);
  const bNumber = toComparableNumber(b);
  if (aNumber !== null && bNumber !== null) return Math.abs(aNumber - bNumber) < NUMERIC_COMPARE_EPSILON;

  if (typeof a === "string" && typeof b === "string") return a.trim() === b.trim();
  return a === b;
}

export function mappedStringSetEquals(a: string[], b: string[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;

  for (const value of setA) {
    if (!setB.has(value)) return false;
  }
  return true;
}

/**
 * Sparse diff of the effective mapper draft against the og defaults. A key is
 * present in the result ONLY when the draft differs from the default, which is
 * exactly what PATCH /records/:id/mapped persists. Returns null when nothing
 * is overridden.
 */
export function diffMappedPayload(
  draft: PartnerDirectoryMappedExpertPayload,
  defaults: PartnerDirectoryMappedExpertPayload,
): Record<string, unknown> | null {
  const overrides: Record<string, unknown> = {};

  // expert_id is a linking control, not a scraped value: keep whenever set.
  if (typeof draft.expert_id === "number") overrides.expert_id = draft.expert_id;
  if (draft.expert_type !== defaults.expert_type) overrides.expert_type = draft.expert_type;

  for (const key of MAPPED_SCALAR_KEYS) {
    if (!mappedScalarEquals(draft[key], defaults[key])) overrides[key] = draft[key];
  }

  if (!mappedStringSetEquals(draft.languages, defaults.languages)) overrides.languages = [...draft.languages];

  const socialLinkOverrides: Record<string, string | null> = {};
  for (const key of MAPPED_SOCIAL_LINK_KEYS) {
    if (!mappedScalarEquals(draft.social_links[key], defaults.social_links[key])) socialLinkOverrides[key] = draft.social_links[key];
  }
  if (Object.keys(socialLinkOverrides).length > 0) overrides.social_links = socialLinkOverrides;

  // Per-record subcategory additions are additive on top of global mappings,
  // so any non-empty list is an override.
  if (draft.subcategory_slugs.length > 0) overrides.subcategory_slugs = [...draft.subcategory_slugs];

  return Object.keys(overrides).length > 0 ? overrides : null;
}

/** Field-level count for the "N fields mapped" header: social links count per key. */
export function countMappedOverrideFields(overrides: Record<string, unknown> | null): number {
  if (!overrides) return 0;

  let count = 0;
  for (const [key, value] of Object.entries(overrides)) {
    count += key === "social_links" && value && typeof value === "object" ? Object.keys(value).length : 1;
  }
  return count;
}
